import React, { useEffect, useState } from "react";
import { MapPin, Navigation, AlertTriangle, Check } from "lucide-react";
import { api } from "@/lib/api";
import { formatCount, formatPct } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import DrillDownPanel from "@/components/ui-fleet/DrillDownPanel";
import MachineListDrillDown from "@/components/ui-fleet/MachineListDrillDown";
import StatTableDrillDown from "@/components/ui-fleet/StatTableDrillDown";
import StatusBadge from "@/components/ui-fleet/StatusBadge";

// India map bounds (approximate)
const MAP = { latMin: 8, latMax: 35, lonMin: 68, lonMax: 97, width: 800, height: 600 };

const project = (lat, lon) => {
  const x = ((lon - MAP.lonMin) / (MAP.lonMax - MAP.lonMin)) * MAP.width;
  const y = MAP.height - ((lat - MAP.latMin) / (MAP.latMax - MAP.latMin)) * MAP.height;
  return { x, y };
};

export default function Geolocation() {
  const [sites, setSites] = useState([]);
  const [machines, setMachines] = useState([]);
  const [breaches, setBreaches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/geo/sites"),
      api.get("/geo/machines"),
      api.get("/geo/breaches"),
    ]).then(([s, m, b]) => {
      setSites(s.data);
      setMachines(m.data);
      setBreaches(b.data);
    });
  }, []);

  const totalActive = machines.filter((m) => m.status === "active").length;
  const totalBreaches = breaches.filter((b) => b.status === "Unresolved").length;

  const siteColor = (status) => {
    switch (status) {
      case "critical": return "var(--status-red)";
      case "warning": return "var(--status-amber)";
      default: return "var(--status-green)";
    }
  };

  // Build a machine list per site_id for drill-downs
  const machinesBySite = sites.reduce((acc, s) => {
    acc[s.id] = machines.filter((m) => m.site_id === s.id);
    return acc;
  }, {});

  return (
    <div data-testid="geo-page">
      <PageHeader title="Geolocation" breadcrumb="Operations"
        right={
          <>
            <span className="text-[12px]" style={{ color: "var(--slate-500)" }}>
              <span className="font-mono">{formatCount(machines.length)}</span> machines · {formatCount(breaches.length)} breaches
            </span>
            <button className="btn-ghost" data-testid="geo-refresh">Refresh</button>
          </>
        }
      />

      {/* KPI strip — drill into national breakdowns */}
      <div className="grid grid-cols-3 gap-5 mb-5">
        <ClickableKPITile
          label="Total Sites"
          value={formatCount(sites.length)}
          sub="Tap for site footprint"
          color="copper"
          onClick={() => setDrill("sites")}
          testId="kpi-geo-sites"
        />
        <ClickableKPITile
          label="Active Machines"
          value={formatCount(totalActive)}
          sub={`${formatPct(machines.length ? totalActive * 100 / machines.length : 0)} of fleet · tap for list`}
          color="green"
          onClick={() => setDrill("active")}
          testId="kpi-geo-active"
        />
        <ClickableKPITile
          label="Active Breaches"
          value={formatCount(totalBreaches)}
          sub={totalBreaches > 0 ? "Unresolved · tap to triage" : "All clear"}
          color={totalBreaches > 0 ? "red" : "green"}
          onClick={() => setDrill("breaches")}
          testId="kpi-geo-breaches"
        />
      </div>

      <div className="grid grid-cols-12 gap-5 mb-5">
        {/* India Map */}
        <GlassCard className="col-span-8 p-5" data-testid="india-map">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Fleet Geolocation — India</h3>
              <p className="text-[12px]" style={{ color: "var(--slate-500)" }}>Click any site to inspect</p>
            </div>
            <div className="flex gap-2">
              <Legend label="Normal" color="var(--status-green)" />
              <Legend label="Warning" color="var(--status-amber)" />
              <Legend label="Critical" color="var(--status-red)" />
            </div>
          </div>
          <div
            className="relative rounded-xl"
            style={{
              height: 460,
              background: "linear-gradient(180deg, rgba(255,252,245,0.4) 0%, rgba(237,232,222,0.6) 100%)",
              border: "1px solid var(--glass-border-btm)",
            }}
          >
            <svg viewBox={`0 0 ${MAP.width} ${MAP.height}`} className="w-full h-full">
              {/* Stylised India outline (rough polygon) */}
              <path
                d="M 220 60 L 320 80 L 400 60 L 470 110 L 510 180 L 590 200 L 620 260 L 660 310 L 640 380 L 580 430 L 520 480 L 460 520 L 420 560 L 380 575 L 340 540 L 320 470 L 280 410 L 240 360 L 200 290 L 180 220 L 200 130 Z"
                fill="rgba(255,252,245,0.6)"
                stroke="rgba(196, 98, 45, 0.3)"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              {/* Geofence dashed circles */}
              {sites.map((s) => {
                const p = project(s.lat, s.lon);
                return (
                  <circle key={`gf-${s.id}`} cx={p.x} cy={p.y} r="32"
                    fill="none" stroke="var(--accent)" strokeOpacity="0.35" strokeDasharray="4 4" strokeWidth="1.5" />
                );
              })}
              {/* Site bubbles */}
              {sites.map((s) => {
                const p = project(s.lat, s.lon);
                const r = 10 + Math.sqrt(s.machine_count) * 3;
                return (
                  <g key={s.id} onClick={() => setSelected(s)} style={{ cursor: "pointer" }}>
                    <circle cx={p.x} cy={p.y} r={r}
                      fill={siteColor(s.status)}
                      fillOpacity={selected?.id === s.id ? 0.95 : 0.78}
                      stroke="white" strokeWidth="2.5" />
                    <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="12" fontFamily="DM Sans" fontWeight="600" fill="white">
                      {s.machine_count}
                    </text>
                    <text x={p.x} y={p.y + r + 14} textAnchor="middle" fontSize="11" fontFamily="DM Sans" fill="var(--slate-900)">
                      {s.city}
                    </text>
                  </g>
                );
              })}
              {/* Breach pulsing dot */}
              {breaches.filter(b => b.status === "Unresolved").map((b, i) => {
                const p = project(b.last_lat, b.last_lon);
                return (
                  <g key={i}>
                    <circle cx={p.x} cy={p.y} r="5" fill="var(--status-red)" />
                    <circle cx={p.x} cy={p.y} r="5" fill="none" stroke="var(--status-red)" strokeWidth="2">
                      <animate attributeName="r" from="5" to="18" dur="1.6s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.9" to="0" dur="1.6s" repeatCount="indefinite" />
                    </circle>
                  </g>
                );
              })}
            </svg>
          </div>
        </GlassCard>

        {/* Selected site / machine detail */}
        <GlassCard className="col-span-4 p-5" data-testid="site-detail">
          {selected ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={16} style={{ color: "var(--accent)" }} />
                <h3 className="font-display text-[17px]" style={{ color: "var(--slate-900)" }}>{selected.name}</h3>
              </div>
              <div className="text-[12px] mb-4" style={{ color: "var(--slate-500)" }}>{selected.city}, {selected.state}</div>
              <div className="font-mono text-[11px] mb-4" style={{ color: "var(--slate-700)" }}>
                {selected.lat.toFixed(6)}, {selected.lon.toFixed(6)}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Stat label="Machines" value={selected.machine_count} />
                <Stat label="Active" value={selected.active} accent />
                <Stat label="Idle" value={selected.idle} />
                <Stat label="Maintenance" value={selected.maintenance} />
                <Stat label="Alerts" value={selected.alert} warn={selected.alert > 0} />
                <Stat label="Distance Today" value={`${selected.distance_today_km}km`} />
              </div>
              <div className="mb-3"><StatusBadge status={selected.status} /></div>
              <div className="glass-card-sm p-3 mt-3">
                <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: "var(--slate-500)" }}>
                  Geofence
                </div>
                <div className="text-[13px]" style={{ color: "var(--slate-900)" }}>
                  Radius <span className="font-mono">{selected.geofence_km} km</span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center" style={{ color: "var(--slate-500)" }}>
              <Navigation size={32} style={{ color: "var(--slate-300)" }} />
              <p className="text-[13px] mt-3">Click any site bubble to view details</p>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Site cards */}
      <div className="grid grid-cols-6 gap-3 mb-5">
        {sites.map((s) => (
          <GlassCard key={s.id} variant="sm" className="p-4 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => { setSelected(s); setDrill(`site:${s.id}`); }} data-testid={`site-card-${s.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full" style={{ background: siteColor(s.status) }} />
              <span className="text-[12px] font-medium" style={{ color: "var(--slate-900)" }}>{s.city}</span>
            </div>
            <div className="font-display text-[22px]" style={{ color: "var(--accent)" }}>{formatCount(s.machine_count)}</div>
            <StatusBadge status={s.status} />
          </GlassCard>
        ))}
      </div>

      {/* Breach log */}
      <GlassCard className="p-5" data-testid="breach-log">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} style={{ color: "var(--status-red)" }} />
          <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Geofence Breach Log</h3>
        </div>
        <table className="w-full text-[12px]">
          <thead>
            <tr style={{ color: "var(--slate-500)" }}>
              {["Machine", "Serial", "Last Coords", "Expected Site", "Distance", "Breach Time", "Status", "Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {breaches.map((b) => (
              <BreachRow
                key={b.id || b.machine_id}
                b={b}
                onAck={(id) => setBreaches((prev) => prev.map((x) => (x.id === id ? { ...x, status: "Acknowledged", acknowledged: true } : x)))}
              />
            ))}
          </tbody>
        </table>
      </GlassCard>

      {/* Drill-down panel */}
      <DrillDownPanel
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        title={
          drill === "sites" ? "Sites Across India"
          : drill === "active" ? "Active Machines"
          : drill === "breaches" ? "Unresolved Breaches"
          : drill && drill.startsWith("site:") ? `${(sites.find((s) => s.id === drill.slice(5)) || {}).name || "Site"} — Machines`
          : ""
        }
        subtitle={
          drill === "sites" ? `${formatCount(sites.length)} operational sites`
          : drill === "active" ? `${formatCount(totalActive)} machines running right now`
          : drill === "breaches" ? `${formatCount(totalBreaches)} unresolved geofence events`
          : drill && drill.startsWith("site:") ? `${(machinesBySite[drill.slice(5)] || []).length} machines deployed`
          : ""
        }
        testId="geo-drilldown"
      >
        {drill === "sites" && (
          <StatTableDrillDown
            rows={sites}
            columns={[
              { key: "city", label: "City" },
              { key: "state", label: "State" },
              { key: "machine_count", label: "Machines", format: (v) => formatCount(v) },
              { key: "active", label: "Active", format: (v) => formatCount(v) },
              { key: "alert", label: "Alerts", format: (v) => formatCount(v) },
              { key: "status", label: "Status" },
            ]}
          />
        )}

        {drill === "active" && (
          <MachineListDrillDown machines={machines.filter((m) => m.status === "active")} />
        )}

        {drill === "breaches" && (
          <StatTableDrillDown
            rows={breaches.filter((b) => b.status !== "Resolved")}
            columns={[
              { key: "machine_id", label: "Machine", mono: true },
              { key: "expected_site", label: "Expected Site" },
              { key: "distance_km", label: "Distance", format: (v) => `${v} km` },
              { key: "breach_time", label: "Seen" },
              { key: "status", label: "Status" },
            ]}
            emptyText="No active breaches."
          />
        )}

        {drill && drill.startsWith("site:") && (
          <MachineListDrillDown machines={machinesBySite[drill.slice(5)] || []} />
        )}
      </DrillDownPanel>
    </div>
  );
}

function Legend({ label, color }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--slate-700)" }}>
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />{label}
    </div>
  );
}

function BreachRow({ b, onAck }) {
  const [acking, setAcking] = useState(false);
  const resolved = b.status === "Resolved";
  const acked = b.acknowledged || b.status === "Acknowledged";

  const ack = async () => {
    if (!b.alert_id) {
      onAck(b.id);
      return;
    }
    setAcking(true);
    try {
      await api.post(`/alerts/${b.alert_id}/acknowledge`);
      onAck(b.id);
    } finally {
      setAcking(false);
    }
  };

  return (
    <tr
      className="border-t"
      style={{
        borderColor: "var(--glass-border-btm)",
        background: acked && !resolved ? "rgba(45, 122, 74, 0.05)" : "transparent",
      }}
      data-testid={`breach-row-${b.machine_id}`}
    >
      <td className="px-3 py-3 font-medium font-mono">{b.machine_id}</td>
      <td className="px-3 py-3 font-mono">{b.serial}</td>
      <td className="px-3 py-3 font-mono">{b.last_lat.toFixed(2)}°N, {b.last_lon.toFixed(2)}°E</td>
      <td className="px-3 py-3">{b.expected_site}</td>
      <td className="px-3 py-3 font-mono">{b.distance_km}km</td>
      <td className="px-3 py-3">{b.breach_time}</td>
      <td className="px-3 py-3">
        <StatusBadge
          status={resolved ? "closed" : acked ? "on_time" : "breached"}
          label={b.status}
        />
      </td>
      <td className="px-3 py-3">
        {resolved ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium" style={{ color: "var(--status-green)" }}>
            <Check size={11} /> Resolved
          </span>
        ) : acked ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium" style={{ color: "var(--status-green)" }}>
            <Check size={11} /> Acknowledged
          </span>
        ) : (
          <button
            onClick={ack}
            disabled={acking}
            className="breach-ack-btn"
            data-testid={`breach-ack-${b.machine_id}`}
          >
            {acking ? "…" : "Acknowledge Breach"}
          </button>
        )}
      </td>
    </tr>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div className="glass-card-sm p-2.5">
      <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--slate-500)" }}>{label}</div>
      <div className="font-display text-[18px]" style={{
        color: warn ? "var(--status-red)" : accent ? "var(--accent)" : "var(--slate-900)"
      }}>{value}</div>
    </div>
  );
}
